import { PERMISSIONS } from "@shared/constants/permissions";
import NoTicketsIcon from "@shared/svg/no-tickets.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuProvider,
  DropdownMenuTrigger,
} from "@shared/ui/DropdownMenu";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import Tooltip from "@shared/ui/Tooltip";
import moment from "moment-timezone";
import { DotsThree, Info } from "phosphor-react";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import usePermissions from "~/hooks/usePermissions";
import { createPreventiveMaintenanceWorkOrder } from "~/services/preventiveMaintenance";
import { useIsCreatePmWorkOrderEnabled } from "~/utils/appFeatures";

type WorkOrderRef = {
  _id?: string;
  ticketId?: string;
  isDeleted?: boolean;
};

type OccurrenceRow = {
  triggerDate?: string | null;
  dueDate?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  canCreateWorkOrder?: boolean;
  workOrderGenerationPending?: boolean;
  workOrder?: WorkOrderRef | null;
};

type OccurrenceScheduleData = {
  past?: OccurrenceRow[] | null;
  upcoming?: OccurrenceRow[] | null;
};

type OpenLimitErrorArgs = {
  code?: string;
  openWorkOrdersCount?: number;
  openWorkOrderLimit?: number;
  immediateWorkOrderCount?: number;
};

type OccurrenceScheduleProps = {
  eventId: string;
  occurrenceSchedule?: OccurrenceScheduleData | null;
  dateFormat: string;
  timeFormat?: string;
  refetchEvents?: () => void | Promise<any>;
  isSharedAssetsPage?: boolean;
};

const COLUMN_GRID =
  "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(200px,220px)] w-full";

const getGraphQLErrors = (exception: any) => [
  ...(exception?.graphQLErrors || []),
  ...(exception?.networkError?.result?.errors || []),
];

const getOpenLimitErrorArgs = (exception: any): OpenLimitErrorArgs | null => {
  for (const err of getGraphQLErrors(exception)) {
    const args = err?.extensions?.additionalArgs;
    if (args?.code === "PME_NEAR_OPEN_WO_LIMIT") return args;
  }
  return null;
};

const getErrorMessage = (exception: any, fallback: string) => {
  const graphQLErrors = getGraphQLErrors(exception);
  const message =
    graphQLErrors?.[0]?.message ||
    exception?.networkError?.result?.errors?.[0]?.message ||
    exception?.message;
  return message || fallback;
};

const isSameDueDate = (a?: string | null, b?: string | null) => {
  if (!a || !b) return false;
  return moment(a).valueOf() === moment(b).valueOf();
};

/** BE says CREATE_PAST_DUE is still queued/running for this past-due row */
const isWorkOrderGenerationPending = (row: OccurrenceRow) =>
  !!row.workOrderGenerationPending;

const HeaderCell = ({
  label,
  tooltipId,
  tooltipText,
}: {
  label: string;
  tooltipId?: string;
  tooltipText?: string;
}) => (
  <div className="flex items-center gap-2xs min-w-0">
    <BodyText
      size={BODY_TEXT_SIZES.X_SMALL}
      className="text-secondary font-semibold"
    >
      {label}
    </BodyText>
    {tooltipId && tooltipText ? (
      <>
        <span
          className="inline-flex text-secondary cursor-pointer shrink-0"
          data-for={tooltipId}
          data-tip={tooltipText}
        >
          <Info size={14} />
        </span>
        <Tooltip id={tooltipId} place="top" />
      </>
    ) : null}
  </div>
);

const formatDateOnly = (
  value: string | null | undefined,
  dateFormat: string,
) => {
  if (!value) return "-";
  return moment(value).format(dateFormat);
};

const formatScheduledDate = (
  start: string | null | undefined,
  end: string | null | undefined,
  dateFormat: string,
) => {
  if (!start && !end) return "-";
  if (start && end) {
    const startMoment = moment(start);
    const endMoment = moment(end);
    if (startMoment.isSame(endMoment, "day")) {
      return startMoment.format(dateFormat);
    }
    return `${startMoment.format(dateFormat)} – ${endMoment.format(
      dateFormat,
    )}`;
  }
  return formatDateOnly(start || end, dateFormat);
};

const OccurrenceSchedule = ({
  eventId,
  occurrenceSchedule,
  dateFormat,
  refetchEvents = () => {},
  isSharedAssetsPage = false,
}: OccurrenceScheduleProps) => {
  const navigate = useNavigate();
  const {
    messages: { preventiveMaintenance: PM, common },
  } = useIntl();
  const { createWorkOrder, loading } = createPreventiveMaintenanceWorkOrder();
  // Creating one changes the asset's schedule and makes a work order.
  const { can, canOpen } = usePermissions();
  const canCreateWorkOrders =
    can(PERMISSIONS.assets.update) && can(PERMISSIONS.workOrders.create);
  const isCreateWorkOrderEnabled = useIsCreatePmWorkOrderEnabled();
  const [creatingDueDate, setCreatingDueDate] = useState<string | null>(null);
  const [showOpenLimitModal, setShowOpenLimitModal] = useState(false);
  const [openLimitErrorArgs, setOpenLimitErrorArgs] =
    useState<OpenLimitErrorArgs | null>(null);
  const [schedule, setSchedule] = useState<OccurrenceScheduleData>({
    past: occurrenceSchedule?.past || [],
    upcoming: occurrenceSchedule?.upcoming || [],
  });

  useEffect(() => {
    setSchedule({
      past: occurrenceSchedule?.past || [],
      upcoming: occurrenceSchedule?.upcoming || [],
    });
  }, [occurrenceSchedule]);

  const openLimitModalBody = useMemo(() => {
    if (!openLimitErrorArgs) return "";
    return (
      PM?.warnings?.openLimitModalBodyCreateWo ??
      PM?.warnings?.openLimitModalBody ??
      ""
    )
      .replace(
        "{immediateWorkOrderCount}",
        String(openLimitErrorArgs.immediateWorkOrderCount ?? 1),
      )
      .replace(
        "{openWorkOrdersCount}",
        String(openLimitErrorArgs.openWorkOrdersCount ?? 0),
      )
      .replace(
        "{openWorkOrderLimit}",
        String(openLimitErrorArgs.openWorkOrderLimit ?? 2000),
      );
  }, [openLimitErrorArgs, PM?.warnings]);

  const pastRows = schedule?.past || [];
  const upcomingRows = schedule?.upcoming || [];
  const rows = [...pastRows, ...upcomingRows];

  const applyCreateResultLocally = (
    row: OccurrenceRow,
    result: {
      workOrder?: WorkOrderRef | null;
      occurrence?: OccurrenceRow | null;
      alreadyExisted?: boolean;
    },
  ) => {
    const occurrence = result?.occurrence;
    const workOrder = result?.workOrder || occurrence?.workOrder;
    if (!workOrder && !occurrence) return;

    setSchedule((prev) => {
      const nextUpcoming = (prev.upcoming || []).filter(
        (item) => !isSameDueDate(item.dueDate, row.dueDate),
      );
      const updatedRow: OccurrenceRow = {
        triggerDate:
          occurrence?.triggerDate ||
          row.triggerDate ||
          new Date().toISOString(),
        dueDate: occurrence?.dueDate || row.dueDate,
        scheduledStart:
          occurrence?.scheduledStart ?? row.scheduledStart ?? null,
        scheduledEnd: occurrence?.scheduledEnd ?? row.scheduledEnd ?? null,
        workOrder: workOrder || null,
        canCreateWorkOrder: false,
        workOrderGenerationPending: false,
      };
      const nextPast = [...(prev.past || []), updatedRow].slice(-8);
      return { past: nextPast, upcoming: nextUpcoming };
    });
  };

  const handleCreateWorkOrder = async (row: OccurrenceRow) => {
    if (
      !isCreateWorkOrderEnabled ||
      !row?.dueDate ||
      !row?.canCreateWorkOrder
    ) {
      return;
    }
    setCreatingDueDate(row.dueDate);
    try {
      const { data } = await createWorkOrder({
        eventId,
        dueDate: row.dueDate,
      });
      const result = data?.createPreventiveMaintenanceWorkOrder;
      applyCreateResultLocally(row, result || {});

      toast(
        <Toast
          type={TOAST_TYPES.SUCCESS}
          message={
            result?.alreadyExisted
              ? PM?.occurrenceSchedule?.workOrderAlreadyExisted ??
                PM?.successWorkOrderCreation
              : PM?.successWorkOrderCreation
          }
        />,
        { closeButton: false },
      );

      // Soft refresh for rolling window; do not block UI / collapse accordion
      Promise.resolve(refetchEvents()).then((refetchResult) => {
        const refreshedEvents =
          refetchResult?.data?.listPreventiveMaintenanceEvents ||
          refetchResult?.data?.listAllPreventiveMaintenanceEvents?.events ||
          null;
        const refreshedEvent = refreshedEvents?.find(
          (event: { _id?: string }) => event?._id === eventId,
        );
        if (refreshedEvent?.occurrenceSchedule) {
          setSchedule({
            past: refreshedEvent.occurrenceSchedule.past || [],
            upcoming: refreshedEvent.occurrenceSchedule.upcoming || [],
          });
        }
      });
    } catch (exception) {
      const openLimitArgs = getOpenLimitErrorArgs(exception);
      if (openLimitArgs) {
        setOpenLimitErrorArgs(openLimitArgs);
        setShowOpenLimitModal(true);
        return;
      }

      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={getErrorMessage(exception, common?.errors?.somethingWrong)}
        />,
        { closeButton: false },
      );
    } finally {
      setCreatingDueDate(null);
    }
  };

  const renderRow = (row: OccurrenceRow, key: string, isLast: boolean) => {
    const isCreating = loading && creatingDueDate === row.dueDate;
    const workOrderLabel =
      row.workOrder?.ticketId || row.workOrder?._id || null;
    const isDeletedWorkOrder = !!row.workOrder?.isDeleted;
    const generationPending = isWorkOrderGenerationPending(row);
    const canCreate =
      canCreateWorkOrders &&
      isCreateWorkOrderEnabled &&
      !isSharedAssetsPage &&
      !!row.canCreateWorkOrder &&
      !isDeletedWorkOrder;
    const workOrderPath = `/app/work/work-orders/${row.workOrder?._id}`;
    // A role that can't open work orders sees which one was made, unlinked.
    const canOpenWorkOrder = canOpen(workOrderPath);
    const canView =
      !!row.workOrder?._id && !isDeletedWorkOrder && canOpenWorkOrder;
    const pendingTooltipId = `pm-wo-pending-${eventId}-${key}`;
    const deletedTooltipId = `pm-wo-deleted-${eventId}-${key}`;

    return (
      <div
        key={key}
        className={`${COLUMN_GRID} border-x border-b border-solid border-primary hover:bg-brand-lightest/40 ${
          isLast ? "rounded-b-xl" : ""
        }`}
      >
        <div className="flex items-center px-md py-md min-w-0">
          <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
            {formatDateOnly(row.triggerDate, dateFormat)}
          </BodyText>
        </div>
        <div className="flex items-center px-md py-md min-w-0">
          <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
            {formatDateOnly(row.dueDate, dateFormat)}
          </BodyText>
        </div>
        <div className="flex items-center px-md py-md min-w-0">
          <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
            {formatScheduledDate(
              row.scheduledStart,
              row.scheduledEnd,
              dateFormat,
            )}
          </BodyText>
        </div>
        <div className="flex items-center gap-md px-md py-md min-w-0 justify-between">
          {workOrderLabel && row.workOrder?._id && isDeletedWorkOrder ? (
            <>
              <span
                className="text-xs font-medium font-manrope text-secondary truncate cursor-default"
                data-for={deletedTooltipId}
                data-tip={
                  PM?.occurrenceSchedule?.workOrderDeleted ??
                  "This work order has been deleted."
                }
              >
                {workOrderLabel}
              </span>
              <Tooltip id={deletedTooltipId} place="top" />
            </>
          ) : workOrderLabel && row.workOrder?._id && canOpenWorkOrder ? (
            <button
              type="button"
              className="text-brand text-xs font-medium font-manrope hover:underline bg-transparent border-0 p-0 cursor-pointer truncate"
              onClick={() => navigate(workOrderPath)}
            >
              {workOrderLabel}
            </button>
          ) : workOrderLabel && row.workOrder?._id ? (
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-primary"
              className="truncate"
            >
              {workOrderLabel}
            </BodyText>
          ) : (
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
              -
            </BodyText>
          )}

          {generationPending && canCreateWorkOrders ? (
            <>
              <span
                className="inline-flex shrink-0"
                data-for={pendingTooltipId}
                data-tip={
                  PM?.occurrenceSchedule?.workOrderGenerationPending ??
                  "Work orders are still being generated, please check back shortly."
                }
              >
                <Button
                  text={PM?.occurrenceSchedule?.createWorkOrder}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  size={BUTTON_SIZES.SMALL}
                  disabled
                  className="shrink-0"
                />
              </span>
              <Tooltip id={pendingTooltipId} place="top" />
            </>
          ) : canCreate ? (
            <Button
              text={PM?.occurrenceSchedule?.createWorkOrder}
              variant={BUTTON_VARIANTS.OUTLINE}
              size={BUTTON_SIZES.SMALL}
              disabled={isCreating}
              className="shrink-0 group-hover:border-brand group-hover:text-brand"
              onClick={() => handleCreateWorkOrder(row)}
            />
          ) : canView ? (
            <DropdownMenuProvider>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-3xl h-3xl flex items-center justify-center bg-transparent border-none cursor-pointer text-secondary hover:text-primary transition-colors shrink-0"
                >
                  <DotsThree size={20} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="bottom"
                align="end"
                className="min-w-[160px] p-0"
              >
                <DropdownMenuItem onSelect={() => navigate(workOrderPath)}>
                  {PM?.previewEvent?.viewTicket}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuProvider>
          ) : (
            <span className="inline-flex w-3xl h-3xl shrink-0" />
          )}
        </div>
      </div>
    );
  };

  const openLimitAlert = (
    <AlertBox
      isOpen={showOpenLimitModal}
      acceptButtonText={PM?.warnings?.openLimitModalButton ?? common?.okay}
      image={<NoTicketsIcon width="130" height="130" />}
      alertContent={
        <>
          <Headline size={HEADLINE_SIZES.X_SMALL}>
            {PM?.warnings?.openLimitModalTitle}
          </Headline>
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-secondary"
            className="mt-sm"
          >
            {openLimitModalBody}
          </BodyText>
        </>
      }
      onAccept={() => {
        setShowOpenLimitModal(false);
        setOpenLimitErrorArgs(null);
      }}
      overlay
    />
  );

  if (!rows.length) {
    return (
      <>
        <div className="rounded-xl border border-solid border-primary px-md py-lg">
          <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
            {PM?.occurrenceSchedule?.empty}
          </BodyText>
        </div>
        {openLimitAlert}
      </>
    );
  }

  return (
    <>
      <div className="w-full overflow-x-auto">
        <div className="min-w-[640px]">
          <div
            className={`${COLUMN_GRID} rounded-t-xl border border-solid border-primary bg-accent`}
          >
            <div className="flex items-center px-md py-md min-w-0">
              <HeaderCell
                label={PM?.occurrenceSchedule?.triggerDate}
                tooltipId={`pm-trigger-date-${eventId}`}
                tooltipText={PM?.occurrenceSchedule?.triggerDateTooltip}
              />
            </div>
            <div className="flex items-center px-md py-md min-w-0">
              <HeaderCell
                label={PM?.occurrenceSchedule?.dueDate}
                tooltipId={`pm-due-date-${eventId}`}
                tooltipText={PM?.occurrenceSchedule?.dueDateTooltip}
              />
            </div>
            <div className="flex items-center px-md py-md min-w-0">
              <HeaderCell
                label={PM?.occurrenceSchedule?.scheduledDate}
                tooltipId={`pm-scheduled-date-${eventId}`}
                tooltipText={PM?.occurrenceSchedule?.scheduledDateTooltip}
              />
            </div>
            <div className="flex items-center px-md py-md min-w-0">
              <HeaderCell label={PM?.occurrenceSchedule?.workOrder} />
            </div>
          </div>

          {rows.map((row, index) =>
            renderRow(
              row,
              `${row.dueDate || index}-${row.triggerDate || index}-${index}`,
              index === rows.length - 1,
            ),
          )}
        </div>
      </div>
      {openLimitAlert}
    </>
  );
};

export default OccurrenceSchedule;
